import { Form, useLoaderData, useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { useEffect, useState, useRef } from 'react';
import { Badge, Stack, Modal, Button } from 'react-bootstrap';
import PermissionErrorPage from '../pages/shared/permissionError';
import { useTopics } from "../contexts/TopicsContext"; // Adjust the path as needed
import TopicSidebar from './topicsBar';
import { useUser } from "../contexts/UserContext";
import { useCourse } from "../contexts/CourseContext";
import '../styles/components/features/question.css';
import { submissionService, problemSetService, questionService, llmService, hintService } from "../services/api";
import { gradeSubmission } from "../services/grading";
import { hiddenTestSummary, isHiddenTest } from "../services/hiddenTests";
import HintFeedbackPanel from "./HintFeedbackPanel";
import { findSchemaDiagram } from "../utils/schemaDiagrams";


import { q, sub } from "framer-motion/client";
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';


let standardout = "";
let resultJSON = "";
let pyodide = null;
let pyodideInitialized = false;
let pyodideLoading = false;
let lastOutput = "";

async function loadPyodideAndPackages() {
  if (pyodideInitialized) return pyodide;
  if (pyodideLoading) {
    throw new Error("Pyodide is already loading");
  }
  
  try {
    pyodideLoading = true;
    pyodide = await window.loadPyodide({
      indexURL: "https://cdn.jsdelivr.net/pyodide/v0.18.1/full/",
      stdout: (msg) => {
        if (msg !== lastOutput) {
          standardout += "\n" + msg;
          lastOutput = msg;
        }
      },
      stdin: (msg) => prompt(msg)
    });
    pyodideInitialized = true;
    return pyodide;
  } finally {
    pyodideLoading = false;
  }
}

const runScript = async (code) => {
  try {
    lastOutput = "";
    const pyodide = await loadPyodideAndPackages();
    standardout = "";
    await pyodide.loadPackagesFromImports(code);
    const results = await pyodide.runPythonAsync(code);
    const stdout = standardout;
    standardout = "";
    return { results, stdout };
  } catch (error) {
    if (error.message === "Pyodide is already loading") {
      return { error: "Please wait, Pyodide is still initializing..." };
    }
    return { error: error.message };
  }
};

// Exporting the loader function
export async function loader({ params }) {
  const question = await getQuestion(params.questionId);
  const localout = localStorage.getItem("value" + question.q_id);
  if (localout) {
    question.userCode = localout;
  }
  return { question };
}

// Exporting the action function
export async function action() {
  const value = localStorage.getItem("value" + localStorage.getItem("qid"));
  let output = "loading...";
  let stdoutput = "";
  const run = async () => {
    const scriptText = value;
    const { out, stdout } = await runScript(scriptText);
    output = out;
    stdoutput = stdout;
  };
  await run();
  localStorage.setItem("stdout", stdoutput);
  return { output, stdoutput };
}

export async function getQuestion(id) {
  const item = localStorage.getItem("Question" + id);
  const question = item ? JSON.parse(item) : {
    q_id: id,
    topic: "topic",
    question: 'Print "Hello World" to the console.',
    answer: 'print("Hello World")',
    stdout: "",
    userCode: ""
  };
  return question;
}



export default function Question() {
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");
  const [isHintLoading, setIsHintLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { questionId } = useParams();
  const editorRef = useRef(null);
  const questionIdRef = useRef(questionId); // track current question for stale-response guard
  const { question } = useLoaderData();
  const { selectedCourse } = useCourse();
  const [question2, setQuestion] = useState({});
  const [attempt, setAttempt] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [studentID, setStudentId] = useState(0);
  const [editorValue, setEditorValue] = useState("");
  const [showSchemaDiagram, setShowSchemaDiagram] = useState(false);
  const [showSqlTablesModal, setShowSqlTablesModal] = useState(false);
  const [activeSqlTableIndex, setActiveSqlTableIndex] = useState(0);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [keystrokeCount, setKeystrokeCount] = useState(0);
  const [difficulty, setDifficulty] = useState("");
  const [showDebug, setShowDebug] = useState(false);
  const [debug, setDebug] = useState("");
  const [sqlTableViews, setSqlTableViews] = useState({});
  const [sqlTableSearch, setSqlTableSearch] = useState({});
  const [sqlTablePages, setSqlTablePages] = useState({});
  const [expectedResultImageUrls, setExpectedResultImageUrls] = useState([]);
  const [hintsUsedForQuestion, setHintsUsedForQuestion] = useState(0);
  const [showHintFeedbackPanel, setShowHintFeedbackPanel] = useState(false);
  const [hintFeedbackSubmitted, setHintFeedbackSubmitted] = useState(false);
  const [hintFeedbackLoading, setHintFeedbackLoading] = useState(false);
  const [selectedHintFeedback, setSelectedHintFeedback] = useState(null);
  const { user } = useUser();
  const { completedQuestions, setCompletedQuestions } = useTopics();
  const { incompleteQuestions, setIncompleteQuestions } = useTopics();
  const [ isIncomplete, setIsIncomplete] = useState(false);

  const navigate = useNavigate();
  const topicsResult = useTopics();
  const topics = topicsResult?.topics || {};
  const questionLanguage = (question2?.language || "python").toLowerCase();
  const isSqlQuestion = questionLanguage === "sql";

  const parseMaybeJson = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    if (typeof value !== "string") return null;
    try {
      return JSON.parse(value);
    } catch {
      try {
        const normalized = value.replace(/'([^']*)'/g, '"$1"');
        return JSON.parse(normalized);
      } catch {
        return null;
      }
    }
  };

  const sqlParametersData = isSqlQuestion ? parseMaybeJson(question2?.parameters) : null;
  const sqlSchemaData = isSqlQuestion ? parseMaybeJson(question2?.schema) : null;
  const sqlReturnsData = isSqlQuestion ? parseMaybeJson(question2?.returns) : null;
  const sqlTestCaseData = isSqlQuestion ? parseMaybeJson(question2?.testCases) : null;
  const hasExample = Boolean((question2?.example || "").toString().trim());
  // The course's own screenshots of the rows a query should return, lifted out
  // of the question PDF when the set was uploaded. Sets uploaded before that
  // have none, and questions without a screenshot in the document never do.
  const expectedResultImageCount = Array.isArray(sqlReturnsData?.expectedResultImages)
    ? sqlReturnsData.expectedResultImages.length
    : 0;
  const resolvedSqlSchema = sqlSchemaData || sqlParametersData?.schema || null;
  // Only present for schemas a course has given us a diagram for; null otherwise.
  const schemaDiagram = isSqlQuestion ? findSchemaDiagram(resolvedSqlSchema) : null;
  const SQL_PAGE_SIZE = 25;
  const hasSqlSchemaTables = Array.isArray(resolvedSqlSchema?.tables) && resolvedSqlSchema.tables.length > 0;
  const sqlInputRows = Array.isArray(sqlTestCaseData?.input)
    ? sqlTestCaseData.input
    : (Array.isArray(sqlTestCaseData?.rows) ? sqlTestCaseData.rows : []);
  const hasSqlTableContent = hasSqlSchemaTables || sqlInputRows.length > 0;

  useEffect(() => {
    setSqlTableViews({});
    setSqlTableSearch({});
    setSqlTablePages({});
    setActiveSqlTableIndex(0);
  }, [questionId]);

  // The screenshots come back as images rather than JSON, so they are fetched
  // here (with the student's session) and held as object URLs until the student
  // moves to another question.
  useEffect(() => {
    // Drop the previous question's screenshots first: the cleanup below has
    // already revoked their URLs, so they can't be shown any more.
    setExpectedResultImageUrls([]);
    if (!isSqlQuestion || expectedResultImageCount === 0) return undefined;

    let loadedUrls = [];
    let abandoned = false;

    Promise.all(
      Array.from({ length: expectedResultImageCount }, (_, index) =>
        questionService.getExpectedResultImage(questionId, index)
          .then((blob) => URL.createObjectURL(blob))
          // A screenshot we can't load just isn't shown; the question still works.
          .catch(() => null))
    ).then((urls) => {
      loadedUrls = urls.filter(Boolean);
      if (abandoned) {
        loadedUrls.forEach(URL.revokeObjectURL);
        return;
      }
      setExpectedResultImageUrls(loadedUrls);
    });

    return () => {
      abandoned = true;
      loadedUrls.forEach(URL.revokeObjectURL);
    };
  }, [questionId, isSqlQuestion, expectedResultImageCount]);

  const parseSqlTestCases = () => {
    return sqlTestCaseData;
  };

  const getSqlColumns = () => {
    const schemaColumns = resolvedSqlSchema?.tables?.[0]?.columns;
    if (Array.isArray(schemaColumns) && schemaColumns.length > 0) {
      return schemaColumns
        .map((column) => column?.name)
        .filter(Boolean);
    }

    const parametersText = question2?.parameters || "";
    const match = parametersText.match(/\(([^)]+)\)/);
    if (!match || !match[1]) return [];
    return match[1]
      .split(",")
      .map((column) => column.trim())
      .filter(Boolean);
  };

  const renderSqlTablePreview = () => {
    if (!isSqlQuestion) return null;
    const parsed = parseSqlTestCases();
    const rows = Array.isArray(parsed?.input)
      ? parsed.input
      : (Array.isArray(parsed?.rows) ? parsed.rows : []);
    const columns = getSqlColumns();
    if (rows.length === 0 || columns.length === 0) return null;

    return (
      <div className="question-example-box">
        <p><b>Input Table Preview:</b></p>
        <table className="table table-bordered table-sm">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`sql-row-${rowIndex}`}>
                {Array.isArray(row)
                  ? row.map((cell, cellIndex) => (
                      <td key={`sql-row-${rowIndex}-cell-${cellIndex}`}>{String(cell)}</td>
                    ))
                  : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSqlTablesContent = () => (
    <div className="question-sql-reference-content">
      {renderSqlSchemaPreview()}
      {renderSqlTablePreview()}
    </div>
  );

  const renderSqlSchemaPreview = () => {
    if (!isSqlQuestion) return null;
    const tables = resolvedSqlSchema?.tables;
    if (!Array.isArray(tables) || tables.length === 0) {
      return (
        <div className="question-example-box">
          <p>
            Schema table information is not available for this question. Please contact your instructor.
          </p>
        </div>
      );
    }

    const getTableKey = (table, index) => {
      if (table?.name) return `${table.name}-${index}`;
      return `table-${index}`;
    };

    const normalizeColumns = (columns) => {
      if (!Array.isArray(columns)) return [];
      return columns
        .map((column) => {
          if (typeof column === "string") {
            return { name: column, type: "" };
          }
          return {
            name: column?.name || "",
            type: column?.type || "",
          };
        })
        .filter((column) => column.name);
    };

    const formatCell = (value) => {
      if (value === null || value === undefined) {
        return <span className="question-null-badge">NULL</span>;
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return String(value);
    };

    const getRowValues = (row, headers) => {
      if (Array.isArray(row)) {
        return headers.map((_, index) => row[index]);
      }
      if (row && typeof row === "object") {
        return headers.map((header) => row[header]);
      }
      return headers.map(() => null);
    };

    const selectedTableIndex = Math.min(activeSqlTableIndex, tables.length - 1);
    const visibleTables = [tables[selectedTableIndex]];

    return (
      <div className="question-example-box">
        <div className="question-sql-table-tabs" role="group" aria-label="Choose a table">
          {tables.map((table, tableIndex) => {
            const tableName = table?.name || `table_${tableIndex + 1}`;
            const isActive = tableIndex === selectedTableIndex;
            return (
              <button
                key={`table-tab-${tableIndex}`}
                type="button"
                aria-pressed={isActive}
                className={`question-sql-table-tab ${isActive ? "active" : ""}`}
                onClick={() => setActiveSqlTableIndex(tableIndex)}
              >
                {tableName}
              </button>
            );
          })}
        </div>
        {visibleTables.map((table) => {
          const tableIndex = tables.indexOf(table);
          const tableKey = getTableKey(table, tableIndex);
          const tableName = table?.name || `table_${tableIndex + 1}`;
          const columns = normalizeColumns(table?.columns);
          const headers = columns.map((column) => column.name);
          const rows = Array.isArray(table?.rows) ? table.rows : [];
          const rowCount = Number.isFinite(table?.rowCount)
            ? table.rowCount
            : rows.length;

          const currentView = sqlTableViews[tableKey] || "data";
          const searchTerm = (sqlTableSearch[tableKey] || "").toLowerCase().trim();
          const filteredRows = rows.filter((row) => {
            if (!searchTerm) return true;
            return getRowValues(row, headers)
              .some((cell) => String(cell ?? "").toLowerCase().includes(searchTerm));
          });

          const totalPages = Math.max(1, Math.ceil(filteredRows.length / SQL_PAGE_SIZE));
          const activePage = Math.min(sqlTablePages[tableKey] || 1, totalPages);
          const startIndex = (activePage - 1) * SQL_PAGE_SIZE;
          const paginatedRows = filteredRows.slice(startIndex, startIndex + SQL_PAGE_SIZE);

          return (
            <div key={tableKey} className="question-sql-table-card">
              <div className="question-sql-table-header">
                <p><b>{tableName}</b></p>
                <div className="question-sql-table-meta">
                  <span className="question-sql-rowcount-badge">{rowCount} rows</span>
                  <div className="question-sql-view-toggle" role="tablist" aria-label={`${tableName} view toggle`}>
                    <button
                      type="button"
                      className={`question-sql-view-button ${currentView === "data" ? "active" : ""}`}
                      onClick={() => setSqlTableViews((prev) => ({ ...prev, [tableKey]: "data" }))}
                    >
                      Data
                    </button>
                    <button
                      type="button"
                      className={`question-sql-view-button ${currentView === "structure" ? "active" : ""}`}
                      onClick={() => setSqlTableViews((prev) => ({ ...prev, [tableKey]: "structure" }))}
                    >
                      Structure
                    </button>
                  </div>
                </div>
              </div>

              {currentView === "structure" && (
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columns.map((column) => (
                      <tr key={`${tableKey}-${column.name}`}>
                        <td>{column.name}</td>
                        <td>{column.type || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {currentView === "data" && (
                <>
                  <div className="question-sql-data-controls">
                    <input
                      type="search"
                      value={sqlTableSearch[tableKey] || ""}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setSqlTableSearch((prev) => ({ ...prev, [tableKey]: nextValue }));
                        setSqlTablePages((prev) => ({ ...prev, [tableKey]: 1 }));
                      }}
                      className="question-sql-search"
                      placeholder="Search rows..."
                    />
                    <div className="question-sql-pagination">
                      <button
                        type="button"
                        className="question-sql-page-button"
                        disabled={activePage === 1}
                        onClick={() => setSqlTablePages((prev) => ({ ...prev, [tableKey]: Math.max(1, activePage - 1) }))}
                      >
                        Prev
                      </button>
                      <span>{activePage} / {totalPages}</span>
                      <button
                        type="button"
                        className="question-sql-page-button"
                        disabled={activePage >= totalPages}
                        onClick={() => setSqlTablePages((prev) => ({ ...prev, [tableKey]: Math.min(totalPages, activePage + 1) }))}
                      >
                        Next
                      </button>
                    </div>
                  </div>

                  {headers.length > 0 && paginatedRows.length > 0 ? (
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          {headers.map((header) => (
                            <th key={`${tableKey}-${header}`}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.map((row, rowIndex) => (
                          <tr key={`${tableKey}-row-${startIndex + rowIndex}`}>
                            {getRowValues(row, headers).map((cell, cellIndex) => (
                              <td key={`${tableKey}-row-${startIndex + rowIndex}-cell-${cellIndex}`}>
                                {formatCell(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="question-sql-empty-data">No rows available for this table.</p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (user) {
      setStudentId(user.studentID);
    }
  }, [user]);

  const questionGet = () => {
    setQuestionLoading(true);
    const cachedQuestion = sessionStorage.getItem(`question_${questionId}`);
    if (cachedQuestion) {
      setQuestion(JSON.parse(cachedQuestion)); // Load from cache
      setQuestionLoading(false);
    } else {
      questionService.getProblem(questionId)
        .then((json) => {
          console.log(json);
          setQuestion(json);
          sessionStorage.setItem(`question_${questionId}`, JSON.stringify(json)); // Save to cache
          setQuestionLoading(false);
        })
        .catch((error) => {
          console.error("Error loading question:", error);
          setQuestionLoading(false);
        });
    }
  };
 
  
  useEffect(() => {
    questionGet();
  }, [questionId]);
  
  // Keep ref in sync with URL so we can ignore stale API responses after navigation
  questionIdRef.current = questionId;

  useEffect(() => {
    async function loadEditorValue() {
      const requestQuestionId = questionId;
      const qid = Number(requestQuestionId);

      // Fetch latest from API using URL questionId (source of truth), not question2 (async, can be stale)
      if (qid && studentID) {
        try {
          const submissions = await submissionService.getSubmissionsByQuestion(qid, studentID);
          // Only apply result if we're still on the same question (avoid race when navigating)
          if (questionIdRef.current !== requestQuestionId) return;
          if (Array.isArray(submissions) && submissions.length > 0) {
            const latestCode = submissions[0].code || "";
            setEditorValue(latestCode);
            localStorage.setItem(`userCode_${requestQuestionId}`, latestCode);
            return;
          }
        } catch (err) {
          if (questionIdRef.current !== requestQuestionId) return;
          console.error("Failed to fetch latest submission:", err);
        }
      }

      // No API data: use localStorage if present (e.g. unsaved work), else empty
      if (questionIdRef.current !== requestQuestionId) return;
      const localCode = localStorage.getItem(`userCode_${requestQuestionId}`);
      if (localCode) {
        setEditorValue(localCode);
        return;
      }

      setEditorValue("");
    }

    loadEditorValue();
    setOutput("Run the code to see the output here.");
    setAttempt(0);
    setDifficulty("");
    setKeystrokeCount(0);
    setStartTime(Date.now());
    setShowHint(false);
    setHasSubmitted(false);
    setShowHintFeedbackPanel(false);
    setHintFeedbackSubmitted(false);
    setSelectedHintFeedback(null);
    // fetch hints used for this question (cap is 3)
    const qid = Number(questionId);
    if (qid && studentID) {
      hintService.getLatestHintIteration(studentID, qid)
        .then((res) => {
          if (questionIdRef.current !== questionId) return;
          setHintsUsedForQuestion(res.hintIteration ?? 0);
        })
        .catch(() => {
          if (questionIdRef.current !== questionId) return;
          setHintsUsedForQuestion(0);
        });
    } else {
      setHintsUsedForQuestion(0);
    }
  }, [questionId, studentID]);

  const refreshHintFeedbackPrompt = async (sid, qid, passed) => {
    if (!sid || !qid || !passed) {
      setShowHintFeedbackPanel(false);
      return;
    }
    try {
      const [hintRes, feedbackRes] = await Promise.all([
        hintService.getLatestHintIteration(sid, qid),
        hintService.getHintFeedback(sid, qid),
      ]);
      const hintCount = hintRes?.hintIteration ?? 0;
      if (hintCount > 0 && feedbackRes?.hasHints && !feedbackRes?.submitted) {
        setShowHintFeedbackPanel(true);
        setHintFeedbackSubmitted(false);
      } else {
        setShowHintFeedbackPanel(false);
        if (feedbackRes?.submitted) {
          setHintFeedbackSubmitted(true);
          setSelectedHintFeedback(feedbackRes.feedback);
        }
      }
    } catch (err) {
      console.error("Error loading hint feedback status:", err);
      setShowHintFeedbackPanel(false);
    }
  };

  useEffect(() => {
    const qid = Number(question2?.questionID || questionId);
    if (studentID && qid && isCompleted) {
      refreshHintFeedbackPrompt(studentID, qid, true);
    }
  }, [studentID, question2?.questionID, questionId, isCompleted]);


  useEffect(() => {

    if (!questionId) return;

    const completed = completedQuestions
      ?.map(String)
      .includes(String(questionId));

    const incomplete = incompleteQuestions
      ?.map(String)
      .includes(String(questionId));

    setIsCompleted(completed);
    setIsIncomplete(!completed && incomplete);

  }, [questionId, completedQuestions, incompleteQuestions]);


  useEffect(() => {
    setStartTime(Date.now());
  }, []);

  const handleKeyDown = () => {
    setKeystrokeCount(prevCount => {
      const newCount = prevCount + 1;
      console.log("Keystroke count:", newCount);
      return newCount;
    });
  };

  async function getHint(studentID, submissionID) {
    try {
      // Use the LLM service to generate hint
      const data = await llmService.generateHint(studentID, submissionID);
      console.log("hint: " + data)
      // Check if the expected 'hint' field exists in the response
      if (!data.hint) {
        throw new Error('No hint returned in response');
      }
  
      // Return the hint and topic list
      return {
        hint: data.hint,        // a string
        hint_topic: data.hint_topic // a list or string, depending on API implementation
        
      };
  
    } catch (error) {
      // Handle any errors that occur during the fetch or parsing
      console.error("Error fetching hint:", error.message);
      throw error; // Re-throw the error for further handling
    }
  }

  async function getLatestSubmissionID(questionID, studentID) {
    try {
      // Use the submission service to get submissions
      const data = await submissionService.getSubmissionsByQuestion(questionID, studentID);
  
      // Check if the response is an array and not empty
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('No submissions found');
      }
      // Extract and return the submissionID from the first item (latest submission)
      return data[0].submissionID;
  
    } catch (error) {
      // Handle any errors that occur during the fetch or parsing
      console.error("Error fetching latest submission ID:", error.message);
      throw error; // Re-throw the error for further handling
    }
  }
  
  

  function handleEditorChange(value) {
    if (value !== null) {
      setEditorValue(value);
      localStorage.setItem(`userCode_${questionId}`, value); // Save per-question

    }
  }

  const handleDifficultyChange = (event) => {
    setDifficulty(event.target.value);
  };

  function handleEditorDidMount(editor) {
    editorRef.current = editor;
    let value = localStorage.getItem("value" + localStorage.getItem("qid"));
    editorRef.current.setValue(value || "");

    editor.onDidType((text) => {
      // Each time the user types, this function is triggered
      // You can call your handleKeyDown here
      handleKeyDown(text);
    });
  }
  useEffect(() => {
  }, [output]);
  async function questionSubmit() {
    setLoading(true);
    const duration = Math.floor((Date.now() - startTime) / 1000);
    const timestamp = new Date().toISOString();
  
    try {
      const code = localStorage.getItem(`userCode_${questionId}`) || "";
      if (!code.trim()) {
        alert("Please write some code before submitting.");
        return false;
      }

      const courseId = selectedCourse?.CRN ?? selectedCourse?.id;
      if (!courseId) {
        alert("Unable to identify the selected course. Please select a course and try again.");
        return false;
      }

      //send payload to lambda
      let problemSet;
      try {
        problemSet = await problemSetService.getProblemSetByTopic(question2.topic, courseId);
      } catch (error) {
        if (error.status === 404) {
          toast.error('This assignment does not have an autograder attached. Please contact your instructor.');
          return false;
        }
        throw error;
      }

      const { data, notice } = await gradeSubmission({
        isSql: isSqlQuestion,
        problemSet,
        code,
        queryName: question2.functionName
      });
      if (notice) {
        toast[notice.level](notice.message);
        return false;
      }

      console.log("data", data);

      //parse data output for question test cases
      const parsedBody = typeof data.body === "string"
        ? JSON.parse(data.body)
        : data.body;

      const resolveFunctionKey = (q) => {
        const direct = (q?.functionName ?? q?.function_name ?? "").trim().toLowerCase();
        if (direct) return direct;
        if (!q?.testCases) return "";
        try {
          const tc = JSON.parse(q.testCases);
          if (Array.isArray(tc) && tc[0]?.entrypoint) {
            return String(tc[0].entrypoint).trim().toLowerCase();
          }
        } catch (_) {
          /* ignore invalid testCases JSON */
        }
        return "";
      };

      const functionKey = resolveFunctionKey(question2);
      const allTests = Array.isArray(parsedBody.tests) ? parsedBody.tests : [];

      let relevantTests = functionKey
        ? allTests.filter((test) =>
            (test?.name || "").toLowerCase().includes(functionKey)
          )
        : [];

      if (functionKey && relevantTests.length === 0) {
        relevantTests = allTests.filter((test) => {
          const m = (test?.name || "").match(/:\s*(\w+)\s*\(/i);
          return m && m[1].toLowerCase() === functionKey;
        });
      }

      if (relevantTests.length === 0 && allTests.length > 0) {
        relevantTests = allTests.filter((test) => {
          const n = (test?.name || "").toLowerCase();
          return n && !n.includes("question hidden");
        });
      }

      const testTotal = relevantTests.length;
      const testCasesPassed = relevantTests.filter(test => test.status === "passed").length;

      // Always surface initial (top-level) print output in Debug
      let debugOutput = "";
      if (data.initial_run_output) {
        debugOutput += `${data.initial_run_output}\n`;
        setDebug(data.initial_run_output || "");
      }

      const overallEmoji = (testCasesPassed === testTotal) && (testTotal !== 0) ? "✅" : "❌";
      const lines = [
        `Autograder Result: Passed ${testCasesPassed}/${testTotal} Test Cases ${overallEmoji}`,
        ""
      ];
      if (!functionKey && allTests.length > 0) {
        lines.push(
          "(Tip: set functionName on the question, or sql_query testCases with entrypoint, so this page counts only that function's tests.)",
          ""
        );
      }

      //handle error before tests run
      if ((!parsedBody.tests || parsedBody.tests.length === 0) && parsedBody.output) {
        const cleanOutput = parsedBody.output.replace(/<[^>]+>/g, "").trim();
        lines.push("❌ Error before tests could run");
        lines.push(`Expected output: Not available`);
        lines.push(`Actual output: ${cleanOutput}`);
        setOutput(lines.join("\n"));
        await submission(code, duration, timestamp, testCasesPassed, false);
        return;
      }

      const relevantTestsNotHidden = relevantTests.filter((test) => {
        const n = (test?.name || "").toLowerCase();
        return n && !n.includes("question hidden") && !isHiddenTest(test);
      });

      //format test case outputs
      for (const t of relevantTestsNotHidden) {
        if (t.status === "passed") {
          lines.push(`✅ Passed ${t.name}`, "");
        } else {
          lines.push(`❌ Failed ${t.name}`);

          const output = t.output?.trim() || "";
          console.log("output:", output);
          let expected = "Not available";
          let actual = "Not available";

          const splitOutput = output.split("\n");
          for (const line of splitOutput) {
            if (line.startsWith("Test Failed:") && line.includes("!=")) {
              const actualExpectedOuput = line.replace("Test Failed:", "").trim().split("!=");
              if (actualExpectedOuput.length === 2) {
                actual = actualExpectedOuput[0].trim().replace(/^'|'$/g, "");
                expected = actualExpectedOuput[1].trim().replace(/^'|'$/g, "");
              }
            }
          }

          if (expected !== "Not available" || actual !== "Not available") {
            lines.push(`Expected output: ${expected}`);
            lines.push(`Actual output: ${actual}`);
          } else if (output) {
            lines.push(output);
          }
          lines.push("");
        }
      }
      lines.push(...hiddenTestSummary(relevantTests));
      setOutput(lines.join("\n"));
  
      const isAllPassed = testCasesPassed === testTotal;
      if (isAllPassed) {
        setIsCompleted(true);
        setIsIncomplete(false);
        if (!completedQuestions.includes(question2.questionID)) {
          setCompletedQuestions([...completedQuestions, question2.questionID]);
          console.log("Updated completedQuestions:", [...completedQuestions, question2.questionID]);
        }
        //remove from list if question was incomplete before
        if (incompleteQuestions.includes(question2.questionID)) {
          setIncompleteQuestions(incompleteQuestions.filter(id => id !== question2.questionID));
        }
      } else {
        setIsCompleted(false);
        setIsIncomplete(true);
        if (!incompleteQuestions.includes(question2.questionID)) {
          setIncompleteQuestions([...incompleteQuestions, question2.questionID]);
        }
      }

      await submission(code, duration, timestamp, testCasesPassed, isAllPassed);
      const qidForFeedback = question2?.questionID ?? Number(questionId);
      if (isAllPassed && studentID && qidForFeedback) {
        await refreshHintFeedbackPrompt(studentID, qidForFeedback, true);
      }
      return true;
    } catch (error) {
      console.error("Error submitting code:", error);
      alert("Failed to run code.");
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function submission(code, duration, timestamp, testCasesPassed, isCompleted) {
    console.log("submission: " + isCompleted);

    // Validate studentID before making API call
    if (!studentID || studentID === 0) {
      console.error("Error storing submission: studentID is not set");
      alert("Unable to store submission: User not authenticated");
      return;
    }

    try {
      const responsePost = await submissionService.storeSubmission(studentID,
        {
          attemptNum: attempt + 1,
          code: code,
          difficulty: difficulty,
          duration: duration,
          keystrokes: keystrokeCount,
          testCasesPassed: testCasesPassed,
          status: isCompleted ? "Passed" : "Failed",
          timestamp: timestamp,
          qid: question2.questionID
        });
    } catch (error) {
      console.error("Error storing submission:", error);
      alert("Failed to store store submission");
    }
  }
  
  // New submit handler that sets hasSubmitted to true after submission
  const handleSubmit = async () => { // Added new handleSubmit function
    const didSubmit = await questionSubmit();
    if (didSubmit) {
      setHasSubmitted(true);
    }
  };

  const handleHintFeedbackSubmit = async (feedbackValue) => {
    if (!studentID || !question2?.questionID || hintFeedbackLoading) return;
    setHintFeedbackLoading(true);
    setSelectedHintFeedback(feedbackValue);
    try {
      await hintService.submitHintFeedback(
        studentID,
        question2.questionID,
        feedbackValue
      );
      setHintFeedbackSubmitted(true);
      setShowHintFeedbackPanel(false);
    } catch (error) {
      console.error("Error submitting hint feedback:", error);
      alert("Could not save your feedback. Please try again.");
      setSelectedHintFeedback(null);
    } finally {
      setHintFeedbackLoading(false);
    }
  };

  const handleUseHint = async () => {
    if (hintsUsedForQuestion >= 3) return;
    setIsHintLoading(true);
    setShowHint(true);
    try {
      let submissionID = await getLatestSubmissionID(question2.questionID, user.studentID);
      let hintAndTopicList = await getHint(user.studentID, submissionID);
      setHint(hintAndTopicList.hint);
      setShowHint(true);
      setHasSubmitted(false);
      setHintsUsedForQuestion((prev) => Math.min(prev + 1, 3));
    } catch (error) {
      console.error("Error fetching hint:", error);
    }
    setIsHintLoading(false);
  };

  const topic = question2.topic;
  const topicKey = Object.keys(topics).find(key => key.toLowerCase() === topic?.toLowerCase());
  const topicQuestions = topicKey ? topics[topicKey].filter(id => id !== null && id !== undefined) : [];
  const currentIdx = topicQuestions.findIndex(id => String(id) === String(question2.questionID));
  const isBeginning = currentIdx === 0;
  const isEnd = currentIdx === topicQuestions.length - 1;
  const handleNextProblem = () => {
    if (topicKey) {
      const nextIndex = (currentIndex + 1) % topicQuestions.length;
      console.log(topicQuestions);
      navigate(`/question/${topicQuestions[nextIndex].questionID}`);
      setCurrentIndex(nextIndex);
    } else {
      console.log(`Topic ${topic} not found`);
    }

  };

  const handlePreviousProblem = () => {
    if (topicKey) {
      // Find the current index in the topic array
      const currentIdx = topicQuestions.findIndex(id => String(id) === String(question2.questionID));
      // Go to previous, wrap around if at start
      const prevIdx = (currentIdx - 1 + topicQuestions.length) % topicQuestions.length;
      navigate(`/question/${topicQuestions[prevIdx].questionID}`);
      setCurrentIndex(prevIdx);
    } else {
      console.log(`Topic ${topic} not found`);
    }
  };

  const handleDownloadSubmissions = async () => {
    try {
      const course = selectedCourse?.CRN ?? selectedCourse?.id;
      const student_id = studentID;
      const topicName = question2.topic;

      if (!course || !student_id || !topicName) {
        alert("Missing course, student, or topic information. Please select a course and ensure you're logged in.");
        return;
      }

      const response = await fetch(
        `${submissionService.baseURL}/submissions/download-submissions/by-id/${course}/${student_id}/${encodeURIComponent(topicName)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": process.env.REACT_APP_API_KEY
          }
        }
      );
      if (!response.ok) {
        alert("Failed to download submissions.");
        return;
      }
      const data = await response.json();
      const extension = data.language === "sql" ? "sql" : "py";
      const blob = new Blob([data.submission], { type: "text/plain;charset=utf-8" });

      // Use file-saver if available, otherwise use a fallback:
      if (window.saveAs) {
        window.saveAs(blob, `${topicName}_submissions.${extension}`);
      } else {
        // Fallback for browsers without file-saver
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${topicName}_submissions.${extension}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert("Error downloading submissions.");
      console.error(err);
    }
  };

  //const parsedTestCases = question2 && question2.testCases ? JSON.parse(question2.testCases) : [];

  return (
    <div id="container" className="question-container">
      <div id="sidebar" className="question-sidebar">
        <TopicSidebar />
      </div>

      {questionLoading && (
        <div className="question-loading-overlay">
          <div className="question-loading-box">
            Loading new question...
          </div>
        </div>
      )}

      <div id="main-content" className="question-main-content">
        <div className="question-content-wrapper">
          <div className="question-header">
            <div className="question-header-left">
              <Stack direction="horizontal">
                {question2.difficulty === "easy" && <Badge bg="success">Easy</Badge>}
                {question2.difficulty === "medium" && <Badge bg="warning">Medium</Badge>}
                {question2.difficulty === "hard" && <Badge bg="danger">Hard</Badge>}
              </Stack>
              {isCompleted && (
                <div className="question-completed-indicator">
                  <span className="question-completed-dot"></span>
                  Completed
                </div>
              )}
              {isIncomplete && (
                <div className="question-incomplete-indicator">
                  <span className="question-incomplete-dot"></span>
                  Incomplete
                </div>
              )}
            </div>

            <h1 className="question-problem-title">
              {isSqlQuestion
                ? (question2.functionName || "SQL Query")
                : `${question2.functionName || "Function"}()`}
            </h1>

            <div className="question-nav-buttons">
              <button
                onClick={handlePreviousProblem}
                disabled={isBeginning}
                className="question-nav-button"
              >
                ← Previous
              </button>
              <button
                onClick={handleNextProblem}
                disabled={isEnd}
                className="question-nav-button"
              >
                Next →
              </button>
            </div>
          </div>

          <div className="question-schema-heading">
            <b>{isSqlQuestion ? "Schema:" : "Parameters:"}</b>
            {isSqlQuestion && hasSqlTableContent && (
              <>
                <button
                  type="button"
                  className="question-schema-button"
                  onClick={() => setShowSqlTablesModal(true)}
                >
                  View Tables
                </button>
              </>
            )}
            {schemaDiagram && (
              <button
                type="button"
                className="question-schema-button"
                onClick={() => setShowSchemaDiagram(true)}
              >
                View diagram
              </button>
            )}
          </div>
          {isSqlQuestion ? (
            hasSqlTableContent ? null : renderSqlSchemaPreview()
          ) : (
            <ReactMarkdown>{question2.parameters}</ReactMarkdown>
          )}

          {isSqlQuestion && hasSqlTableContent && (
            <>
              <Modal
                show={showSqlTablesModal}
                onHide={() => setShowSqlTablesModal(false)}
                size="xl"
                centered
                scrollable
                dialogClassName="question-sql-tables-dialog"
              >
                <Modal.Header>
                  <Modal.Title>
                    Choose a table for {question2.functionName || "this question"}
                  </Modal.Title>
                  <button
                    type="button"
                    className="question-sql-tables-close"
                    aria-label="Close tables"
                    onClick={() => setShowSqlTablesModal(false)}
                  >
                    ×
                  </button>
                </Modal.Header>
                <Modal.Body className="question-sql-tables-modal-body">
                  {renderSqlTablesContent()}
                </Modal.Body>
              </Modal>
            </>
          )}

          {schemaDiagram && (
            <Modal
              show={showSchemaDiagram}
              onHide={() => setShowSchemaDiagram(false)}
              size="xl"
              centered
              scrollable
            >
              <Modal.Header closeButton>
                <Modal.Title>{schemaDiagram.title}</Modal.Title>
              </Modal.Header>
              <Modal.Body className="question-schema-diagram-body">
                {/* These diagrams are dense, so let it render at full width and
                    scroll rather than shrinking it to fit. */}
                <img
                  src={schemaDiagram.src}
                  alt={schemaDiagram.alt}
                  className="question-schema-diagram"
                />
              </Modal.Body>
            </Modal>
          )}

          {/* SQL questions have no "Expected Result" section: it said the same
              thing on every one of them (write a query). What this particular
              query should return is the screenshot under the description. */}
          {!isSqlQuestion && (
            <>
              <p>
                <b>Returns:</b>
              </p>
              <ReactMarkdown>{question2.returns}</ReactMarkdown>
            </>
          )}

          <p>
            <b>
              {isSqlQuestion && question2.functionName
                ? `Description (${question2.functionName}):`
                : "Description:"}
            </b>
          </p>
          <ReactMarkdown>{question2.description}</ReactMarkdown>

          {expectedResultImageUrls.length > 0 && (
            <>
              <p>
                <b>Your query should return:</b>
              </p>
              <div className="question-example-box">
                {expectedResultImageUrls.map((url) => (
                  <img
                    key={url}
                    src={url}
                    alt={`The rows ${question2.functionName || "this query"} should return`}
                    className="question-expected-result-image"
                  />
                ))}
              </div>
            </>
          )}

          {hasExample && (
            <>
              <p>
                <b>Example:</b>
              </p>
              <div className="question-example-box">
                <pre>{question2.example}</pre>
              </div>
            </>
          )}

          <div id="prompt" dangerouslySetInnerHTML={{ __html: question2.question }} />

          <Form method="post">
            <div id="userCode" className="question-editor-container">
              <Editor
                id="editor"
                height="300px"
                language={isSqlQuestion ? "sql" : "python"}
                value={editorValue}
                onChange={handleEditorChange}
                onMount={handleEditorDidMount}
                onKeyPress={handleKeyDown}
              />
            </div>

            <div className="question-note">
              {isSqlQuestion
                ? "Note: Write only your query (SELECT ... FROM ...). You do not need to create a view; that is done for you."
                : "Note: If you would like to see the Python shell output (print statements) for debugging, be sure to call your function in the code block above, just like you would in IDLE or another Python IDE."
              }
            </div>

            <div className="question-actions-container">
              <div className="question-settings-section">
                <h4>How difficult was this question for you?</h4>
                <select
                  value={difficulty}
                  onChange={handleDifficultyChange}
                  className="question-difficulty-select"
                >
                  <option value="">Select your rating</option>
                  <option value={0}>Very Easy</option>
                  <option value={1}>Easy</option>
                  <option value={2}>Medium</option>
                  <option value={3}>Hard</option>
                  <option value={4}>Very Hard</option>
                </select>
              </div>

              <div className="question-settings-section">
                <h4>Actions</h4>
                <button
                  onClick={handleDownloadSubmissions}
                  className="question-download-button"
                >
                  {isSqlQuestion
                    ? "Download All Queries For This Set (.sql)"
                    : "Download All Submissions For This Set"}
                </button>
              </div>

              <div className="question-actions-row">
                <button
                  onClick={handleSubmit}
                  disabled={loading || difficulty === ""}
                  className={`question-submit-button ${hasSubmitted ? 'with-hint' : ''}`}
                >
                  {loading ? "Submitting..." : "Submit Code"}
                </button>

                {hasSubmitted && (
                  hintsUsedForQuestion < 3 ? (
                    <button
                      onClick={handleUseHint}
                      className="question-hint-button"
                    >
                      Hint
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="question-hint-button question-hint-button-capped"
                      disabled
                      title="No hints left for this question"
                    >
                      Hint (3/3)
                    </button>
                  )
                )}

                <button
                  type="button"
                  onClick={() => setShowDebug((prev) => !prev)}
                  className="question-debug-button"
                >
                  {showDebug ? "Hide Debug" : "Show Debug"}
                </button>
              </div>
            </div>

            {showHint && (
              <div className="question-hint-container">
                <h4>Hint:</h4>
                {isHintLoading ? (
                  <p className="question-hint-loading">Loading hint...</p>
                ) : (
                  <pre className="question-hint-content">
                    {hint}
                  </pre>
                )}
              </div>
            )}

            {showHintFeedbackPanel && !hintFeedbackSubmitted && (
              <HintFeedbackPanel
                onSubmit={handleHintFeedbackSubmit}
                isSubmitting={hintFeedbackLoading}
                selectedValue={selectedHintFeedback}
              />
            )}
            {hintFeedbackSubmitted && selectedHintFeedback !== null && (
              <p className="hint-feedback-thanks" role="status">
                Thanks for your feedback on the hints for this question.
              </p>
            )}

            {showDebug && (
              <div className="question-debug-container">
                <h4>Debug Information:</h4>
                <pre className="question-debug-content">
                  {debug || "No print output available."}
                </pre>
              </div>
            )}

            <div className="question-output-container">
              <h4>Output:</h4>
              <pre className="question-output-content">
                {loading ? "Processing your code submission..." : output || "Run the code to see the output here."}
              </pre>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}
